import {
  globalMessageHandler,
  useDialog,
  useGrid,
  UseGridReturnType,
} from "common";
import I18n from "i18n-js";
import { createContext, useEffect, useState } from "react";
import {
  copyWaiverById,
  getWaiverLimit,
  getWaiverService,
  loadWaiverById,
  updateWaiverStatus,
  WaiverFormItem,
  WaiverListItem,
  WaiverStatus,
} from "./waiverApi";

export type UseWaiverListReturnType = {
  createSelectorOpen?: boolean;
  previewOpen?: boolean;
  createSelecterDialog: any;
  previewDialog: any;
  previewItem: null | WaiverListItem;
  setCreateSelectorOpen: Function;
  handlePreviewOpen: (id: string | number) => Promise<void>;
  handleCopy: (id: string | number) => Promise<void>;
  handleStatusChange: (
    id: string | number,
    status: WaiverStatus,
    confirm?: boolean
  ) => Promise<void>;
  grid: UseGridReturnType;
  waiverLimit: number;
  waiverCount: number;
  canAddWaiver: boolean;
};

export const WaiverListContext = createContext<UseWaiverListReturnType>(
  {} as UseWaiverListReturnType
);

export default function useWaiverList(): UseWaiverListReturnType {
  const service = getWaiverService();
  const grid = useGrid<WaiverFormItem, WaiverListItem>({
    comaSeparatedSortPrarms: true,
    storageType: "urlParams",
    id: "Waivers",
    loadColumns: true,
    translationCategory: "Waiver",
    confirmAttribute: "name",

    exportFilter: filter => ({
      ClientSearch: filter,
    }),
    ...service,
  });
  const [createSelectorOpen, setCreateSelectorOpen, createSelecterDialog] =
    useDialog();
  const [previewOpen, setPreviewOpen, previewDialog] = useDialog();
  const [previewItem, setPreviewItem] = useState<null | WaiverListItem>(null);
  const [waiverLimit, setWaiverLimit] = useState(0);
  const [waiverCount, setWaiverCount] = useState(0);
  const [canAddWaiver, setCanAddWaiver] = useState(false);

  const handlePreviewOpen = async (id: string | number) => {
    const waiver = await loadWaiverById(id);

    if (waiver.success) {
      setPreviewItem(waiver.data.data);
      setPreviewOpen(true);
    }
  };

  const handleCopy = async (id: string | number) => {
    const response = await copyWaiverById(id);

    if (response.success) {
      grid.load();
      globalMessageHandler.success(I18n.t("Waiver.successCloned"));
    }
  };

  const handleStatusChange = async (
    id: string | number,
    status: WaiverStatus,
    confirm: boolean = false
  ) => {
    const response = await updateWaiverStatus(id, status, confirm);

    if (response.success) {
      grid.load();
    } else if (!confirm && response.error?.form_errors?.confirm?.[0]) {
      globalMessageHandler.confirm(
        response.error.form_errors.confirm[0],
        async () => {
          await handleStatusChange(id, status, true);
        }
      );
    }
  };

  useEffect(() => {
    updateWaiverLimit();
  }, []);

  async function updateWaiverLimit() {
    const result = await getWaiverLimit();

    if (result?.data?.data) {
      const resultLimit = result.data.data?.limit;
      const resultCount = result.data.data?.count;
      setWaiverLimit(resultLimit);
      setWaiverCount(resultCount);

      setCanAddWaiver(resultCount < resultLimit);
    } else {
      setCanAddWaiver(false);
    }
  }

  return {
    grid,
    createSelectorOpen,
    previewOpen,
    createSelecterDialog,
    waiverLimit,
    waiverCount,
    canAddWaiver,
    previewDialog,
    previewItem,
    setCreateSelectorOpen,
    handlePreviewOpen,
    handleCopy,
    handleStatusChange,
  };
}
